import { useEffect, useRef } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";

/** How long a finger has to stay put before it is holding rather than tapping. */
const HOLD_MS = 450;
/** A press that travels further than this is a scroll, and never a hold. */
const HOLD_SLOP = 10;

/**
 * Hold this, rather than tap it.
 *
 * The app's answer to a card that means two things. A tap is the common one and
 * happens on the whole card; the rarer one is held for. It is used twice, in
 * opposite directions, and that is deliberate — each surface puts the *frequent*
 * intention on the tap:
 *
 * - a pot's row in the fridge opens the pot on a tap and renames it on a hold
 * - a pot's tile in the add sheet eats a portion on a tap and opens the pot on
 *   a hold, because the sheet is open in order to log something
 *
 * Pointer events, so a mouse held down on a desktop does what a thumb does on a
 * phone without a second implementation. Three things have to be true for the
 * gesture to be worth having:
 *
 * - **A scroll is not a hold.** These cards sit in lists and rails scrolled with
 *   exactly this finger, in exactly this place, so the timer dies the moment the
 *   pointer travels.
 * - **The hold does not also tap.** The click that follows the release would
 *   otherwise fire the tap's action on top of the hold's, so the caller checks
 *   `held` and defaults the click away — a rename *and* a navigation, or a
 *   portion logged on the way to the pot's page.
 * - **The system menu keeps out of it.** Holding a link on iOS raises the
 *   preview sheet, and holding text anywhere raises the selection handles;
 *   `contextmenu` is refused here and the CSS turns both off on the card.
 */
export function useHold(onHold: () => void) {
  const timer = useRef<number | null>(null);
  const from = useRef<{ x: number; y: number } | null>(null);
  const held = useRef(false);

  const stop = () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
    from.current = null;
  };

  useEffect(() => stop, []);

  return {
    held,
    handlers: {
      onPointerDown: (event: ReactPointerEvent) => {
        if (event.pointerType === "mouse" && event.button !== 0) return;
        stop();
        held.current = false;
        from.current = { x: event.clientX, y: event.clientY };
        timer.current = window.setTimeout(() => {
          timer.current = null;
          held.current = true;
          // Felt rather than seen, on the phones that do it at all: something
          // is about to change under a finger that is still down.
          navigator.vibrate?.(8);
          onHold();
        }, HOLD_MS);
      },
      onPointerMove: (event: ReactPointerEvent) => {
        const start = from.current;
        if (!start) return;
        if (
          Math.abs(event.clientX - start.x) > HOLD_SLOP ||
          Math.abs(event.clientY - start.y) > HOLD_SLOP
        ) {
          stop();
        }
      },
      onPointerUp: stop,
      onPointerCancel: stop,
      onContextMenu: (event: { preventDefault: () => void }) => event.preventDefault(),
    },
  };
}
